import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  AlertTriangle, CreditCard, RefreshCw, LogOut, 
  Mail, Phone, HelpCircle, Clock
} from 'lucide-react';

export default function AccountSuspended({ reason, message, tenantName }) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const getReasonContent = () => {
    switch (reason) {
      case 'trial_expired':
        return {
          icon: Clock,
          title: 'Free Trial Expired',
          description: 'Your 14-day free trial has ended. Subscribe now to continue using all features.',
          actionText: 'Choose a Plan',
          actionPath: '/billing/plans',
          color: 'amber'
        };
      case 'suspended':
        return {
          icon: AlertTriangle,
          title: 'Account Suspended',
          description: 'Your account has been suspended due to a payment issue. Please update your payment method to restore access.',
          actionText: 'Update Payment',
          actionPath: '/billing/update-payment',
          color: 'red'
        };
      case 'cancelled':
        return {
          icon: AlertTriangle,
          title: 'Subscription Cancelled',
          description: 'Your subscription has been cancelled. Resubscribe to regain access to your data and features.',
          actionText: 'Resubscribe',
          actionPath: '/billing/plans',
          color: 'gray'
        };
      default:
        return {
          icon: AlertTriangle,
          title: 'Access Restricted',
          description: message || 'Please contact support for assistance.',
          actionText: 'Contact Support',
          actionPath: '/support',
          color: 'amber'
        };
    }
  };

  const content = getReasonContent();
  const IconComponent = content.icon;

  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'bg-red-100 text-red-600',
      title: 'text-red-800',
      button: 'bg-red-600 hover:bg-red-700'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'bg-amber-100 text-amber-600',
      title: 'text-amber-800',
      button: 'bg-amber-600 hover:bg-amber-700'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      icon: 'bg-gray-100 text-gray-600',
      title: 'text-gray-800',
      button: 'bg-gray-600 hover:bg-gray-700'
    }
  };

  const colors = colorClasses[content.color];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <Card className={`p-8 ${colors.bg} ${colors.border} border-2`}>
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 rounded-full ${colors.icon} flex items-center justify-center`}>
              <IconComponent className="w-10 h-10" />
            </div>
          </div>

          {/* Title */}
          <h1 className={`text-2xl font-bold text-center ${colors.title} mb-2`}>
            {content.title}
          </h1>

          {/* Business Name */}
          {tenantName && (
            <p className="text-center text-gray-600 mb-4">
              {tenantName}
            </p>
          )}

          {/* Description */}
          <p className="text-center text-gray-700 mb-8">
            {content.description}
          </p>

          {/* Primary Action */}
          <Button
            className={`w-full ${colors.button} text-white mb-4`}
            onClick={() => navigate(content.actionPath)}
            data-testid="subscription-action-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {content.actionText}
          </Button>

          {/* Secondary Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </Card>

        {/* Help Section */}
        <Card className="p-6 mt-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Need Help?
          </h3>
          
          <div className="space-y-3">
            <a 
              href="mailto:support@integrityhomecare.com"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Mail className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">Email Support</p>
                <p className="text-xs text-gray-500">support@integrityhomecare.com</p>
              </div>
            </a>
            
            <a 
              href="tel:+18005551234"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Phone className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">Phone Support</p>
                <p className="text-xs text-gray-500">1-800-555-1234</p>
              </div>
            </a>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Your data is safe and will be available once your subscription is restored.
          </p>
        </Card>
      </div>
    </div>
  );
}

// Hook to check subscription status and show suspended screen
export function useSubscriptionCheck() {
  const [subscriptionError, setSubscriptionError] = React.useState(null);

  const checkSubscription = React.useCallback(async (response) => {
    if (response?.status === 403) {
      try {
        const data = await response.json();
        if (data.error === 'subscription_required') {
          setSubscriptionError(data);
          return true;
        }
      } catch (e) {
        // Not a subscription error
      }
    }
    return false;
  }, []);

  const clearError = React.useCallback(() => {
    setSubscriptionError(null);
  }, []);

  return { subscriptionError, checkSubscription, clearError };
}
